
import { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useToast } from './use-toast'
import { useAuth } from './use-auth'
import { useChildStore } from '@/lib/store/child'
import { useCaregiverStore } from '@/lib/store/caregiver'
import { CaregiverService } from '@/lib/services/caregiver-service'
import { ChildService } from '@/lib/services/child-service'
import { logger } from '@/lib/services/logger-service'

export const useDashboard = () => {
  const [addChildOpen, setAddChildOpen] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const [loadingError, setLoadingError] = useState<string | null>(null)
  
  const { toast } = useToast()
  const navigate = useNavigate()
  const { session } = useAuth()
  const { 
    children: storeChildren,
    selectedChild,
    setSelectedChild,
    setChildren,
    setLoading,
    setError 
  } = useChildStore()
  const { setCaregiverId, setFullProfile } = useCaregiverStore()

  // Get caregiver data
  useEffect(() => {
    const initializeCaregiver = async () => {
      logger.info('[DASHBOARD] ===== INITIALIZING CAREGIVER =====', {
        hasSession: !!session,
        userId: session?.user?.id,
        userEmail: session?.user?.email,
        userRole: session?.user?.user_metadata?.role,
        userRoles: session?.user?.user_metadata?.roles
      })
      
      try {
        const caregiver = await CaregiverService.getCurrentCaregiver()
        
        if (caregiver) {
          logger.info('[DASHBOARD] Caregiver found and initialized', {
            caregiverId: caregiver.id
          })
          setCaregiverId(caregiver.id)
          
          // Fetch detailed profile data using the RPC function
          try {
            const profileData = await CaregiverService.getCaregiverProfileData(caregiver.id)
            if (profileData && profileData.length > 0) {
              logger.info('Fetched comprehensive caregiver profile data', {
                caregiverId: caregiver.id,
                hasSpecialists: profileData[0].specialists?.length > 0
              })
              setFullProfile(profileData[0])
            }
          } catch (profileError) {
            logger.error('Failed to fetch comprehensive profile data', { 
              error: profileError,
              caregiverId: caregiver.id 
            })
            // Continue with basic profile data even if comprehensive data fails
          }
        } else {
          logger.warn('[DASHBOARD] !!! NO CAREGIVER FOUND FOR USER !!!', {
            userId: session?.user?.id,
            userEmail: session?.user?.email,
            userRole: session?.user?.user_metadata?.role,
            userRoles: session?.user?.user_metadata?.roles,
            message: 'This user may be an admin or specialist without a caregiver record'
          })
        }
      } catch (error) {
        logger.error('[DASHBOARD] Failed to initialize caregiver', { 
          error,
          userId: session?.user?.id,
          userEmail: session?.user?.email 
        })
        setLoadingError('Failed to load caregiver information')
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load caregiver information",
        })
      }
    }

    initializeCaregiver()
  }, [setCaregiverId, setFullProfile, toast])

  // Check authentication
  useEffect(() => {
    if (!session) {
      logger.warn('No active session found, redirecting to home')
      toast({
        title: "Session Expired",
        description: "Please sign in to continue using the dashboard",
      })
      navigate('/')
    }
  }, [session, navigate, toast])

  // Retry fetch on error
  const retryFetch = useCallback(async () => {
    if (loadingError) {
      setLoadingError(null)
      await refetchChildren()
    }
  }, [loadingError])

  // Fetch children data
  const { 
    data: queriedChildren,
    refetch: refetchChildren,
    isLoading: isLoadingChildren,
    error: childrenError
  } = useQuery({
    queryKey: ['children'],
    queryFn: async () => {
      logger.info('[DASHBOARD] ===== FETCHING CHILDREN DATA =====', {
        userId: session?.user?.id,
        userEmail: session?.user?.email,
        userRole: session?.user?.user_metadata?.role
      })
      
      setLoading(true)
      try {
        const caregiver = await CaregiverService.getCurrentCaregiver()
        
        if (!caregiver) {
          logger.warn('[DASHBOARD] No caregiver found - returning empty children array', {
            userId: session?.user?.id,
            userEmail: session?.user?.email,
            userRole: session?.user?.user_metadata?.role,
            userRoles: session?.user?.user_metadata?.roles,
            message: 'User may be new or caregiver record not created yet'
          })
          // Return empty array instead of throwing - let user add children
          setChildren([])
          return []
        }

        logger.info('[DASHBOARD] Fetching children for caregiver', {
          caregiverId: caregiver.id
        })

        const children = await ChildService.getChildrenByCaregiver(caregiver.id)
        
        // Only update children in store if they've changed
        if (JSON.stringify(children) !== JSON.stringify(storeChildren)) {
          logger.info('Updating children in store from query', {
            childCount: children.length
          })
          setChildren(children)
        }
        
        logger.info('[DASHBOARD] Successfully fetched children', {
          childCount: children.length
        })
        
        setError(null)
        setLoadingError(null)
        return children
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to fetch children'
        logger.error('[DASHBOARD] Failed to fetch children', {
          error: message,
          userId: session?.user?.id,
          userEmail: session?.user?.email
        })
        setError(message)
        setLoadingError(message)
        throw error
      } finally {
        setLoading(false)
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000)
  })

  // Set initial selected child if none selected
  useEffect(() => {
    if (storeChildren.length > 0 && !selectedChild) {
      logger.debug('Setting initial selected child', {
        childId: storeChildren[0].id,
        childName: storeChildren[0].name
      })
      setSelectedChild(storeChildren[0])
    }
  }, [storeChildren.length, selectedChild, setSelectedChild]);

  // When children are updated but the selected child is no longer in the list
  // (this can happen if the selected child was deleted or the list was filtered)
  useEffect(() => {
    if (storeChildren.length > 0 && selectedChild) {
      // Check if the selected child is still in the list
      const childExists = storeChildren.some(child => child.id === selectedChild.id)
      if (!childExists) {
        logger.debug('Selected child no longer exists in the list, selecting first available child', {
          previousChildId: selectedChild.id,
          newChildId: storeChildren[0].id
        })
        setSelectedChild(storeChildren[0])
      }
    }
  }, [storeChildren, selectedChild?.id, setSelectedChild]);

  const handleChildSelect = (childId: string) => {
    const child = storeChildren.find(c => c.id === childId)
    if (child) {
      logger.debug('User selected child', {
        childId: child.id,
        childName: child.name
      })
      setSelectedChild(child)
    }
  }

  const handleAddChildSuccess = async () => {
    setAddChildOpen(false)
    await refetchChildren()
  }

  return {
    addChildOpen,
    setAddChildOpen,
    chatOpen,
    setChatOpen,
    children: storeChildren,
    selectedChild,
    isLoadingChildren,
    childrenError: childrenError || loadingError,
    handleChildSelect,
    handleAddChildSuccess,
    retryFetch
  }
}
